import { omit } from "lodash-es";
import * as api from "@/helpers/api";
import { ALL_MEMO_LIMIT, DEFAULT_MEMO_LIMIT } from "@/helpers/consts";
import { writeMemoFeedCache } from "@/utils/memoFeedCache";
import store, { useAppSelector } from "../";
import { updateLoadingStatus, createMemo, deleteMemo, patchMemo, replaceMemos, upsertMemos, LoadingStatus } from "../reducer/memo";
import { useMemoCacheStore } from "../v1";

export const convertResponseModelMemo = (memo: Memo): Memo => {
  return {
    ...memo,
    createdTs: memo.createdTs * 1000,
    updatedTs: memo.updatedTs * 1000,
    displayTs: memo.displayTs * 1000,
  };
};

export const convertTagSuggestionResp = (resp: Record<string, number>) => {
  const tags = Object.keys(resp);
  return tags
    .map((tag) => {
      return {
        tag: tag,
        sim: resp[tag],
      };
    })
    .sort((a, b) => b.sim - a.sim);
};

const persistMemoFeed = (username: string) => {
  void writeMemoFeedCache(username, store.getState().memo.memos);
};

export const useMemoStore = () => {
  const state = useAppSelector((state) => state.memo);
  const memoCacheStore = useMemoCacheStore();

  const fetchMemoById = async (memoId: MemoId) => {
    const { data } = await api.getMemoById(memoId);
    const memo = convertResponseModelMemo(data);
    store.dispatch(upsertMemos([memo]));
    return memo;
  };

  return {
    state,
    getState: () => {
      return store.getState().memo;
    },
    fetchMemos: async (username = "", limit = DEFAULT_MEMO_LIMIT, offset = 0, tag?: string) => {
      const memoFind: MemoFind = {
        rowStatus: "NORMAL",
        limit,
        offset,
      };
      if (username) {
        memoFind.creatorUsername = username;
      }

      store.dispatch(updateLoadingStatus("fetching"));
      const { data } = await api.getMemoList(memoFind);
      const fetchedMemos = data.map((m) => convertResponseModelMemo(m));

      store.dispatch(offset === 0 ? replaceMemos(fetchedMemos) : upsertMemos(fetchedMemos));
      store.dispatch(updateLoadingStatus(fetchedMemos.length === limit ? "incomplete" : "complete"));

      if (offset === 0) {
        const usernames = new Set(fetchedMemos.map((memo) => memo.creatorUsername).filter(Boolean));
        usernames.forEach((creatorUsername) => {
          persistMemoFeed(creatorUsername);
        });
      }

      return fetchedMemos;
    },
    fetchAllMemos: async (limit = ALL_MEMO_LIMIT, offset = 0, includeTrashed = false) => {
      const memoFind: MemoFind = {
        rowStatus: "NORMAL",
        limit: limit,
        offset: offset,
      };

      const { data: normalMemos } = await api.getMemoList(memoFind);
      let fetchedMemos = normalMemos.map((m) => convertResponseModelMemo(m));

      if (includeTrashed) {
        const archivedMemoFind: MemoFind = {
          rowStatus: "ARCHIVED",
          limit: 1000,
        };
        const { data: archivedMemos } = await api.getMemoList(archivedMemoFind);
        const convertedArchivedMemos = archivedMemos.map((m) => convertResponseModelMemo(m));
        fetchedMemos = [...fetchedMemos, ...convertedArchivedMemos];
      }

      store.dispatch(upsertMemos(fetchedMemos));

      return fetchedMemos;
    },
    fetchArchivedMemos: async () => {
      const memoFind: MemoFind = {
        rowStatus: "ARCHIVED",
      };
      const { data } = await api.getMemoList(memoFind);
      const archivedMemos = data.map((m) => {
        return convertResponseModelMemo(m);
      });
      return archivedMemos;
    },
    setLoadingStatus: (status: LoadingStatus) => {
      store.dispatch(updateLoadingStatus(status));
    },
    hydrateMemoFeed: (memos: Memo[]) => {
      if (store.getState().memo.memos.length === 0 && memos.length > 0) {
        store.dispatch(replaceMemos(memos));
      }
    },
    fetchMemoById,
    getMemoById: async (memoId: MemoId) => {
      for (const m of state.memos) {
        if (m.id === memoId) {
          return m;
        }
      }

      return await fetchMemoById(memoId);
    },
    getLinkedMemos: async (memoId: MemoId): Promise<Memo[]> => {
      const currentMemo = state.memos.find((m) => m.id === memoId);
      if (!currentMemo) return [];

      console.log("getLinkedMemos - 当前备忘录ID:", memoId);
      console.log("getLinkedMemos - 当前备忘录内容:", currentMemo.content);

      const referencedIds = new Set<number>();

      const patterns = [
        /#(\d+)(?!\]|\))/g, // #数字 格式（排除[]和()中的数字）
        /\[.*?\]\(\/m\/(\d+)\)/g, // [任意文本](/m/数字) 格式
        /\[.*?\]\((\d+)\)/g, // [任意文本](数字) 格式
        /(?<!\[.*?)\((\d+)\)/g, // (数字) 格式（不在[]后面）
      ];

      for (const pattern of patterns) {
        const matches = currentMemo.content.matchAll(pattern);
        for (const match of Array.from(matches)) {
          const id = parseInt(match[1]);
          if (!isNaN(id) && id !== memoId) {
            referencedIds.add(id);
            console.log(`getLinkedMemos - 当前备忘录 #${memoId} 引用了备忘录 #${id}, 匹配文本: "${match[0]}", 匹配模式: ${pattern}`);
          }
        }
      }

      const result = state.memos.filter((memo) => referencedIds.has(memo.id));
      console.log(
        "getLinkedMemos - 上游备忘录:",
        result.map((m) => m.id)
      );

      const missingMemos = Array.from(referencedIds).filter((id) => !result.find((m) => m.id === id));
      if (missingMemos.length > 0) {
        console.log("getLinkedMemos - 尝试获取缺失的备忘录:", missingMemos);
        for (const id of missingMemos) {
          try {
            const memo = await fetchMemoById(id);
            result.push(memo);
          } catch (error) {
            console.error(`获取备忘录 #${id} 失败:`, error);
          }
        }
      }

      return result;
    },
    getMemosByReferenceId: async (memoId: MemoId): Promise<Memo[]> => {
      const result = state.memos.filter((memo) => {
        if (memo.id === memoId) return false;

        const hasReference = memo.relationList?.some((relation) => relation.type === "REFERENCE" && relation.relatedMemoId === memoId);

        return hasReference;
      });

      console.log(
        "getMemosByReferenceId - 下游备忘录:",
        result.map((m) => m.id)
      );
      return result;
    },
    getComments: async (memoId: MemoId): Promise<Memo[]> => {
      console.log("getComments - 查找备忘录ID:", memoId, "的评论");

      const result = state.memos.filter((memo) => {
        if (memo.id === memoId) return false;

        const hasComment = memo.relationList?.some((relation) => relation.type === "COMMENT" && relation.relatedMemoId === memoId);

        if (hasComment) {
          console.log(`getComments - 找到评论: 备忘录 #${memo.id} 评论了 #${memoId}`);
        }

        return hasComment;
      });

      console.log(
        "getComments - 评论列表:",
        result.map((m) => m.id)
      );
      return result;
    },
    createMemo: async (memoCreate: MemoCreate) => {
      const { data } = await api.createMemo(memoCreate);
      const createdMemo = convertResponseModelMemo(data);
      store.dispatch(createMemo(createdMemo));
      memoCacheStore.setMemoCache(createdMemo);
      persistMemoFeed(createdMemo.creatorUsername);

      // 派发 memo-created 事件
      const event = new CustomEvent("memo-created", {
        detail: { memo: createdMemo },
      });
      document.dispatchEvent(event);

      return createdMemo;
    },
    createMemoVersion: async (versionCreate: { memoId: MemoId; content: string; createdTs: number; resourceList: ResourceId[] }) => {
      try {
        // 保存版本到本地存储
        const storageKey = `memo_versions_${versionCreate.memoId}`;
        const versions = JSON.parse(localStorage.getItem(storageKey) || "[]");

        // 添加新版本，使用精确的时间戳
        versions.push({
          id: `${versionCreate.memoId}_${versions.length + 1}`,
          memoId: versionCreate.memoId,
          content: versionCreate.content,
          createdTs: versionCreate.createdTs,
          updatedTs: versionCreate.createdTs,
          resourceList: versionCreate.resourceList,
        });

        // 保存到本地存储
        localStorage.setItem(storageKey, JSON.stringify(versions));

        return true;
      } catch (error) {
        console.error("保存版本失败:", error);
        return false;
      }
    },
    getMemoVersions: async (memoId: MemoId) => {
      try {
        // 从本地存储获取版本历史
        const storageKey = `memo_versions_${memoId}`;
        const versions = JSON.parse(localStorage.getItem(storageKey) || "[]");
        return versions;
      } catch (error) {
        console.error("获取版本历史失败:", error);
        return [];
      }
    },
    patchMemo: async (memoPatch: MemoPatch) => {
      const previousMemo = store.getState().memo.memos.find((memo) => memo.id === memoPatch.id);
      store.dispatch(patchMemo(memoPatch));
      if (previousMemo) persistMemoFeed(previousMemo.creatorUsername);

      let updatedMemo: Memo;
      try {
        const { data } = await api.patchMemo(memoPatch);
        updatedMemo = convertResponseModelMemo(data);
        store.dispatch(patchMemo(updatedMemo));
        memoCacheStore.setMemoCache(updatedMemo);
        persistMemoFeed(updatedMemo.creatorUsername);
      } catch (error) {
        if (previousMemo) {
          store.dispatch(upsertMemos([previousMemo]));
          memoCacheStore.setMemoCache(previousMemo);
          persistMemoFeed(previousMemo.creatorUsername);
        }
        throw error;
      }

      // 派发 memo-updated 事件
      const event = new CustomEvent("memo-updated", {
        detail: { memo: updatedMemo },
      });
      document.dispatchEvent(event);

      return updatedMemo;
    },
    pinMemo: async (memoId: MemoId) => {
      await api.pinMemo(memoId);
      store.dispatch(
        patchMemo({
          id: memoId,
          pinned: true,
        })
      );
    },
    unpinMemo: async (memoId: MemoId) => {
      await api.unpinMemo(memoId);
      store.dispatch(
        patchMemo({
          id: memoId,
          pinned: false,
        })
      );
    },
    deleteMemoById: async (memoId: MemoId): Promise<void> => {
      const deletedMemo = store.getState().memo.memos.find((memo) => memo.id === memoId);
      store.dispatch(deleteMemo(memoId));
      memoCacheStore.deleteMemoCache(memoId);
      if (deletedMemo) persistMemoFeed(deletedMemo.creatorUsername);

      try {
        await api.deleteMemo(memoId);
      } catch (error) {
        if (deletedMemo) {
          store.dispatch(upsertMemos([deletedMemo]));
          memoCacheStore.setMemoCache(deletedMemo);
          persistMemoFeed(deletedMemo.creatorUsername);
        }
        throw error;
      }

      // 派发删除成功事件，用于通知其他组件更新
      const event = new CustomEvent("memo-deleted", {
        detail: { memoId: memoId },
      });
      document.dispatchEvent(event);
    },
    // 检查评论卡片是否应该在主页显示
    shouldShowCommentInHome: (memoId: MemoId): boolean => {
      const storageKey = "hide_comments_in_home";
      const hiddenComments = JSON.parse(localStorage.getItem(storageKey) || "[]");
      return !hiddenComments.includes(memoId);
    },

    // 设置评论卡片在主页的显示状态
    setCommentVisibilityInHome: (memoId: MemoId, shouldShow: boolean): void => {
      const storageKey = "hide_comments_in_home";
      let hiddenComments = JSON.parse(localStorage.getItem(storageKey) || "[]");

      if (shouldShow) {
        // 从隐藏列表中移除
        hiddenComments = hiddenComments.filter((id: MemoId) => id !== memoId);
      } else {
        // 添加到隐藏列表中
        if (!hiddenComments.includes(memoId)) {
          hiddenComments.push(memoId);
        }
      }

      localStorage.setItem(storageKey, JSON.stringify(hiddenComments));

      // 触发事件通知其他组件更新
      const event = new CustomEvent("comment-visibility-changed", {
        detail: { memoId, shouldShow },
      });
      document.dispatchEvent(event);
    },
    fetchExplorePageMemos: async (limit = DEFAULT_MEMO_LIMIT, offset = 0) => {
      let fetchedMemos: Memo[] = [];
      try {
        const { data: userList } = await api.getUserList();
        if (userList && userList.length > 0) {
          const memoFind: MemoFind = {
            rowStatus: "NORMAL",
            limit: limit,
            offset: offset,
            visibility: "PUBLIC",
          };
          const { data } = await api.getMemoList(memoFind);
          fetchedMemos = data.map((m) => convertResponseModelMemo(m));
        }
      } catch (error) {
        const possibleAdminIds = [1, 2, 101];
        let success = false;

        for (const adminId of possibleAdminIds) {
          if (success) break;

          try {
            const memoFind: MemoFind = {
              rowStatus: "NORMAL",
              limit: limit,
              offset: offset,
              creatorId: adminId,
              visibility: "PUBLIC",
            };

            const { data } = await api.getMemoList(memoFind);
            fetchedMemos = data.map((m) => convertResponseModelMemo(m));
            success = true;
          } catch (err) {
            console.log(`尝试使用用户ID ${adminId} 获取公开笔记失败`);
          }
        }

        if (!success) {
          try {
            const memoFind: MemoFind = {
              rowStatus: "NORMAL",
              limit: limit,
              offset: offset,
            };

            const { data } = await api.getAllMemos(memoFind);
            fetchedMemos = data.map((m) => convertResponseModelMemo(m)).filter((m) => m.visibility === "PUBLIC");
          } catch (err) {
            console.error("所有尝试获取公开笔记的方法都失败:", err);
          }
        }
      }

      store.dispatch(upsertMemos(fetchedMemos));

      return fetchedMemos;
    },
  };
};
